import { Component, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ModalService } from '@indice/ng-components';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { catchError, EMPTY, filter, finalize, mergeMap, of, tap } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import { AccountOwner, ContactUsRequest, PortalApiService } from 'src/app/services/portal-api.service';
import { SignPromptActionsModalComponent } from './sign-prompt-actions-modal/sign-prompt-actions-modal.component';
import { CommonModule } from '@angular/common';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { RecaptchaModule, RecaptchaFormsModule, RecaptchaComponent } from 'ng-recaptcha';
import { ToasterService, ToastType } from 'src/app/services/toaster.service';
import { RouterLink } from '@angular/router';
import { response } from 'express';
import { LanguageRouterLinkDirective } from 'src/app/shared/directives/language-router-link.directive';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-contact',
  standalone: true,
  imports: [ReactiveFormsModule,TranslocoDirective,CommonModule,LanguageRouterLinkDirective,NgxMaskDirective, LoaderComponent, RecaptchaModule, RecaptchaFormsModule, TranslocoPipe],
  templateUrl: './contact.component.html',
  styleUrl: './contact.component.css',
  providers: [provideNgxMask()],
})
export class ContactComponent {
  @ViewChild('recaptcha') recaptcha!: RecaptchaComponent;

  loading = false;
  form = new UntypedFormGroup({
    fullName: new UntypedFormControl('', Validators.required),
    email: new UntypedFormControl('', [Validators.required, Validators.email]),
    message: new UntypedFormControl('', Validators.required),
    acceptTerms: new UntypedFormControl(false, Validators.requiredTrue),
    recaptcha: new UntypedFormControl('', Validators.required), // Recaptcha form control
  });

  protected siteKey = environment.recaptchaPublicKey;
  protected isLoggedIn: boolean = false;
  protected acceptedTerms: boolean = false;

  private toastTranslations: any;

  constructor(
    private _portalApi: PortalApiService,
    private _authService: AuthService,
    private _modalService: ModalService,
    private _toasterService: ToasterService,
    private _translocoService: TranslocoService
  ) {
    this.form.get('acceptTerms')?.valueChanges.subscribe((checked: boolean) => {
      this.acceptedTerms = checked;
    });

    this._translocoService.selectTranslateObject('contact.toasts').pipe(
      tap((translations) => {
        this.toastTranslations = translations;
      })
    ).subscribe();
  }

  ngOnInit(): void {
    this._authService.isLoggedIn().pipe(
      tap(s => this.isLoggedIn = s),
      mergeMap((res: boolean) => {
        if (res) {
          return this._portalApi.getAccountOwner()
        } else {
          this.openSignIn();
          return of(null)
        }
      }),
      filter(res => res !== null),
      tap(res => {
        if (res) {
          this.form.get('fullName')?.setValue(`${res.firstName ?? ''} ${res.lastName ?? ''}`);
          this.form.get('email')?.setValue(res.email ?? '');
        }
      }),
      catchError(err => of(AccountOwner))
    ).subscribe();
  }

  openSignIn() {
    this._modalService.show(SignPromptActionsModalComponent, {
      keyboard: true,
      class: 'h-dvh md:h-auto',
    });
  }

  get isFormValid(): boolean {
    return this.isLoggedIn &&
      this.acceptedTerms &&
      this.form.get('fullName')?.value &&
      this.form.get('email')?.value &&
      this.form.get('message')?.value &&
      this.form.get('recaptcha')?.value
  }

  submitForm() {
    if (this.form.valid && this.isLoggedIn) {
      this.loading = true;

      let captchaResponse = this.form.get('recaptcha')?.value;
      this._portalApi.validateRecaptcha(captchaResponse).pipe(
        catchError((err) => {
          this.recaptcha.reset();
          this._toasterService.showBanner(ToastType.Error, this.toastTranslations['captchaValidationError'], '', 3000);
          return EMPTY;
        }),
        mergeMap(() => {
          var body = new ContactUsRequest({
            name: this.form.get('fullName')?.value,
            email: this.form.get('email')?.value,
            message: this.form.get('message')?.value
          });

          return this._portalApi.contactUs(body).pipe(
            catchError((err) => {
              this._toasterService.showBanner(ToastType.Error, this.toastTranslations['messageSubmitError'], '', 3000);
              return EMPTY;
            })
          )
        }),
        tap(() => {
          this.form.reset();
          this.recaptcha.reset();
          this._toasterService.showBanner(ToastType.Success, this.toastTranslations['messageSubmitSuccess'], '', 3000);
        }),
        finalize(() => {
          this.loading = false;
        })
      ).subscribe();
    }
  }

  public onCaptchaClick() {
    let currentCaptcha = this.form.get('recaptcha')?.value;

    if (!currentCaptcha || currentCaptcha == '') {
      this.recaptcha.execute();
    } else {
      this.recaptcha.reset();
    }
  }
}
